import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { UserButton, useAuth } from '@clerk/clerk-react';
import { Sun, Moon } from 'lucide-react';
import { getStoredTheme, setStoredTheme, type Theme } from '../lib/theme';

const navAll = [
  { to: '/', label: 'Status' },
  { to: '/dashboards', label: 'Dashboards' },
  { to: '/call-history', label: 'Call history' },
  { to: '/settings', label: 'Settings' },
];

function canAccessDashboards(role: string, canAccess: boolean): boolean {
  return role === 'supervisor' || role === 'admin' || role === 'superadmin' || !!canAccess;
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { getToken } = useAuth();
  const [theme, setTheme] = useState<Theme>(() => getStoredTheme());
  const [dashboardAccess, setDashboardAccess] = useState<boolean | null>(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = await getToken();
      if (!token || cancelled) return;
      const base = typeof window !== 'undefined' ? window.location.origin : '';
      try {
        const res = await fetch(`${base}/api/users/me`, { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled || !res.ok) return;
        const data = await res.json();
        const user = data?.user;
        if (user) setDashboardAccess(canAccessDashboards(user.role ?? 'agent', !!(user.can_access_custom_dashboards ?? 0)));
        else setDashboardAccess(false);
      } catch {
        setDashboardAccess(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken]);
  const nav = dashboardAccess === null ? navAll : navAll.filter((item) => item.to !== '/dashboards' || dashboardAccess);

  const toggleTheme = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    setStoredTheme(next);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1.5rem',
          padding: '0.75rem 1.5rem',
          background: 'var(--widget-bg)',
          borderBottom: '1px solid var(--header-border)',
        }}
      >
        <span style={{ fontWeight: 700, marginRight: '1rem' }}>ipSCAPE Wallboard</span>
        <nav style={{ display: 'flex', gap: '0.5rem', flex: 1 }}>
          {nav.map(({ to, label }: { to: string; label: string }) => (
            <Link
              key={to}
              to={to}
              style={{
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                textDecoration: 'none',
                color: location.pathname === to || (to !== '/' && location.pathname.startsWith(to)) ? 'var(--accent)' : 'var(--text-muted)',
              }}
            >
              {label}
            </Link>
          ))}
        </nav>
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: '6px',
            border: '1px solid var(--header-border)',
            background: 'transparent',
            color: 'var(--text)',
            cursor: 'pointer',
          }}
        >
          {theme === 'dark' ? <Sun size={20} strokeWidth={2} /> : <Moon size={20} strokeWidth={2} />}
        </button>
        <UserButton afterSignOutUrl="/" />
      </header>
      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          padding:
            location.pathname === '/' ||
            location.pathname.startsWith('/call-history') ||
            location.pathname.startsWith('/dashboards')
              ? 0
              : '1.5rem',
          overflow: 'hidden',
        }}
      >
        {children}
      </main>
    </div>
  );
}
